import { TimeService, getMomentTz } from "@dexteel/mesf-core";
import { DateTimePicker } from "@mui/x-date-pickers";
import { Moment } from "moment-timezone";
import { useRef } from "react";
import { useTimerContext } from "../../../context/timer.provider";

interface DateTimeDE {
	value: Date | null;
	format?: string;
	label?: string;
	disabled?: boolean;
	disableOpenPicker?: boolean;
	onChange: (value: Date | null) => void;
	onBlur: (value: Date | null) => void;
	error?: string;
	minDateTime?: Date | null;
	maxDateTime?: Date | null;
	disableFuture?: boolean;
}

const moment = getMomentTz();

export const DateTimePickerDE = ({
	value,
	format,
	label,
	onChange,
	onBlur,
	disabled = false,
	disableOpenPicker = false,
	error = "",
	minDateTime = null,
	maxDateTime = null,
	disableFuture = true,
}: DateTimeDE) => {
	const additionalProps: { [key: string]: any } = {};
	if (format) additionalProps["format"] = format;

	const { pause, resume } = useTimerContext();
	const isPickerOpenRef = useRef(false);

	const onFocus = () => {
		pause();
	};

	const handleOpen = () => {
		isPickerOpenRef.current = true;
		pause();
	};

	const handleClose = () => {
		isPickerOpenRef.current = false;
		resume();
	};

	// When maxDateTime is provided, we use it instead of disableFuture to ensure minute-level precision
	const effectiveDisableFuture = disableFuture && !maxDateTime;

	return (
		<DateTimePicker
			ampm={false}
			disableFuture={effectiveDisableFuture}
			disableOpenPicker={disableOpenPicker}
			disabled={disabled}
			timezone={TimeService.getInstance().getServerTimeZone()}
			label={label}
			value={moment(value)}
			minDateTime={minDateTime ? moment(minDateTime) : undefined}
			maxDateTime={maxDateTime ? moment(maxDateTime) : undefined}
			onOpen={handleOpen}
			onClose={handleClose}
			onChange={(value: Moment | null) => {
				onChange(value?.toDate() || null);
			}}
			onAccept={(value: Moment | null) => {
				// Executes when selection is confirmed from the modal
				onChange(value?.toDate() || null);
				onBlur(value?.toDate() || null);
			}}
			openTo={
				format?.includes("HH:mm") && !format?.includes("DD/MM/YYYY")
					? "hours"
					: "day"
			}
			views={
				format?.includes("HH:mm") && !format?.includes("DD/MM/YYYY")
					? ["hours", "minutes"]
					: ["year", "month", "day", "hours", "minutes"]
			}
			timeSteps={{ minutes: 1 }}
			slotProps={{
				textField: {
					variant: "outlined",
					fullWidth: true,
					onFocus,
					onBlur: () => {
						// Only resume if picker is not open (avoids resuming when clicking to open picker)
						if (!isPickerOpenRef.current) {
							resume();
						}
						onBlur(value || null);
					},
					error: !!error,
					helperText: error,
					InputLabelProps: {
						style: {
							fontSize: "14px",
						},
					},
					InputProps: {
						style: {
							height: "35px",
							maxWidth: "100%",
						},
					},
					inputProps: {
						sx: {
							fontSize: "16px !important",
						},
					},
				},
			}}
			{...additionalProps}
		/>
	);
};
